import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { ExpertSelector, DEMO_EXPERT_ID } from './ExpertSelector';
import { JournalistGallery } from './JournalistGallery';
import { ApproachSelector } from './ApproachSelector';
import { QuestionCurator } from './QuestionCurator';
import { BriefingViewer } from './BriefingViewer';
import { LanguageSelector } from './LanguageSelector';

interface Interview {
  id?: string;
  site_id: string;
  expert_id: string;
  expert_name?: string;  // Auto-populated from expert
  expert_title?: string;
  expert_company?: string;
  expert_bio?: string;
  expert_photo_url?: string;
  expert_linkedin?: string;
  expert_website?: string;
  expert_email?: string;
  journalist_id: string;
  approach: string;
  approach_data: any;
  language?: string;
  generated_questions?: any[];
  selected_questions?: any[];
  briefing_content?: string;
  status: string;
  demo_mode?: boolean;
  interview_category?: string;
  approach_source?: any;
}

interface InterviewWizardProps {
  open: boolean;
  onClose: () => void;
  interview?: Interview | null;
  onSuccess: () => void;
  initialExpertId?: string;
  initialApproach?: string;
}

const steps = [
  { id: 1, title: 'Expert Information', description: 'Enter expert details' },
  { id: 2, title: 'Select Journalist', description: 'Choose AI journalist persona' },
  { id: 3, title: 'Interview Approach', description: 'Define interview focus' },
  { id: 4, title: 'Generate Questions', description: 'AI-powered question generation' },
  { id: 5, title: 'Review & Create', description: 'Final review and briefing' }
];

export function InterviewWizard({ open, onClose, interview, onSuccess, initialExpertId, initialApproach }: InterviewWizardProps) {
  const { selectedSite } = useSite();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<Interview>>({
    expert_id: '',
    journalist_id: '',
    approach: 'product_deep_dive',
    approach_data: {},
    language: 'en',
    status: 'draft',
    demo_mode: false,
    interview_category: 'product'
  });

  const [interviewId, setInterviewId] = useState<string | null>(interview?.id || null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    if (open && interview) {
      setFormData(interview);
      setInterviewId(interview.id || null);
    } else if (open && !interview) {
      // Reset for new interview, with optional initial values
      setFormData({
        expert_id: initialExpertId || '',
        journalist_id: '',
        approach: initialApproach || 'product_deep_dive',
        approach_data: {},
        language: 'en',
        status: 'draft',
        demo_mode: false,
        interview_category: 'product'
      });
      setInterviewId(null);
      // If expert is pre-selected, skip to step 2
      setCurrentStep(initialExpertId ? 2 : 1);
      setError(null);
      setSuccess(null);
      setIsDemoMode(false);
    }
  }, [open, interview, initialExpertId, initialApproach]);

  const canProgress = () => {
    switch (currentStep) {
      case 1:
        // For demo mode, expert_id will be the special demo ID
        return formData.expert_id || isDemoMode;
      case 2:
        return formData.journalist_id;
      case 3:
        return formData.approach;
      case 4:
        return formData.selected_questions && formData.selected_questions.length > 0;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    setError(null);
    setSuccess(null);

    // Save interview and handle special step transitions
    if (currentStep === 3) {
      // Step 3 → 4: Save and generate questions
      try {
        setSaving(true);
        await saveInterview();
        setSaving(false);

        // For question_wheel, business_story, and expert_matched approaches, use pre-selected questions
        // instead of generating new ones via AI
        if (formData.approach === 'question_wheel' || formData.approach === 'business_story' || formData.approach === 'expert_matched') {
          // Use the full question objects stored by ApproachSelector
          const preSelectedQuestions = formData.approach_data?.selected_wheel_question_objects ||
                                       formData.approach_data?.selected_template_question_objects ||
                                       formData.approach_data?.selected_matched_question_objects || [];

          if (preSelectedQuestions.length === 0) {
            setError('Please select at least one question from the wheel or template');
            return;
          }

          // Format pre-selected questions for the question curator
          // These will be shown in step 4 for final review
          const formattedQuestions = preSelectedQuestions.map((q: any, idx: number) => ({
            id: q.id,
            text: q.text,
            category: q.category || 'core',
            priority: q.priority || (idx < 5 ? 'high' : 'medium'),
            selected: true,
            source: q.source || (formData.approach === 'question_wheel' ? 'question_wheel' : 'business_template'),
            metrics: q.metrics || null
          }));

          setFormData((prev) => ({
            ...prev,
            generated_questions: formattedQuestions,
            selected_questions: formattedQuestions
          }));
          setCurrentStep(4);
        } else {
          // Standard approaches - generate questions via AI
          setGenerating(true);
          await generateQuestions();
          setCurrentStep(4);
        }
      } catch (err) {
        console.error('Error saving or generating questions:', err);
        setError(err instanceof Error ? err.message : 'Failed to save or generate questions');
      } finally {
        setSaving(false);
        setGenerating(false);
      }
    } else if (currentStep === 4) {
      // Step 4 → 5: Generate briefing
      try {
        setGenerating(true);
        await generateBriefing();
        setCurrentStep(5);
      } catch (err) {
        console.error('Error generating briefing:', err);
        setError(err instanceof Error ? err.message : 'Failed to generate briefing');
      } finally {
        setGenerating(false);
      }
    } else if (currentStep < 3) {
      // Steps 1-2: Just save and continue
      try {
        setSaving(true);
        await saveInterview();
        setCurrentStep(currentStep + 1);
      } catch (err) {
        console.error('Error saving interview:', err);
        setError(err instanceof Error ? err.message : 'Failed to save interview');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleBack = () => {
    setError(null);
    setSuccess(null);
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const saveInterview = async () => {
    if (!selectedSite?.id) {
      throw new Error('No site selected');
    }

    // Determine interview category based on approach
    let interviewCategory = 'product';
    if (formData.approach === 'business_story') {
      interviewCategory = 'business';
    } else if (formData.approach === 'category') {
      interviewCategory = 'category';
    }

    // Extract approach source from approach_data if present
    const approachSource = formData.approach_data?.approachSource || null;

    // NEW SIMPLIFIED API: Only send expertId, backend auto-populates expert data
    const payload = {
      siteId: selectedSite.id,
      expertId: isDemoMode ? null : formData.expert_id,
      journalistId: formData.journalist_id,
      approach: formData.approach,
      approachData: formData.approach_data,
      language: formData.language || 'en',
      generatedQuestions: formData.generated_questions,
      selectedQuestions: formData.selected_questions,
      briefingContent: formData.briefing_content,
      status: formData.status,
      demoMode: isDemoMode,
      interviewCategory: interviewCategory,
      approachSource: approachSource
    };

    let response;
    if (interviewId) {
      // Update existing
      response = await authFetch(`/api/interviews/${interviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } else {
      // Create new
      response = await authFetch('/api/interviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to save interview');
    }

    const data = await response.json();
    if (!interviewId) {
      setInterviewId(data.interview.id);
    }
    return data.interview;
  };

  const generateQuestions = async () => {
    if (!interviewId) {
      throw new Error('Interview not created yet');
    }

    // Generate questions
    const questionsResponse = await authFetch(`/api/interviews/${interviewId}/generate-questions`, {
      method: 'POST'
    });

    if (!questionsResponse.ok) {
      throw new Error('Failed to generate questions');
    }

    const questionsData = await questionsResponse.json();
    setFormData((prev) => ({
      ...prev,
      generated_questions: questionsData.questions,
      selected_questions: questionsData.questions // Pre-select all questions by default
    }));
  };

  const generateBriefing = async () => {
    if (!interviewId) {
      throw new Error('Interview not created yet');
    }

    // Save selected questions first
    await saveInterview();

    // Generate briefing
    const briefingResponse = await authFetch(`/api/interviews/${interviewId}/generate-briefing`, {
      method: 'POST'
    });

    if (!briefingResponse.ok) {
      throw new Error('Failed to generate briefing');
    }

    const briefingData = await briefingResponse.json();
    setFormData((prev) => ({
      ...prev,
      briefing_content: briefingData.briefing
    }));
  };

  const handleFinish = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update status to briefing_ready since we have a briefing
      const updatedFormData = {
        ...formData,
        status: 'briefing_ready'
      };
      setFormData(updatedFormData);

      // Save final state with updated status
      // We need to pass the updated status since setState is async
      if (!selectedSite?.id) {
        throw new Error('No site selected');
      }

      // Determine interview category based on approach
      let interviewCategory = 'product';
      if (updatedFormData.approach === 'business_story') {
        interviewCategory = 'business';
      } else if (updatedFormData.approach === 'category') {
        interviewCategory = 'category';
      }

      const approachSource = updatedFormData.approach_data?.approachSource || null;

      const payload = {
        siteId: selectedSite.id,
        expertId: isDemoMode ? null : updatedFormData.expert_id,
        journalistId: updatedFormData.journalist_id,
        approach: updatedFormData.approach,
        approachData: updatedFormData.approach_data,
        language: updatedFormData.language || 'en',
        generatedQuestions: updatedFormData.generated_questions,
        selectedQuestions: updatedFormData.selected_questions,
        briefingContent: updatedFormData.briefing_content,
        status: updatedFormData.status,
        demoMode: isDemoMode,
        interviewCategory: interviewCategory,
        approachSource: approachSource
      };

      const response = await authFetch(`/api/interviews/${interviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save interview');
      }

      setSuccess(isDemoMode
        ? 'Demo interview created! Experience the interview flow yourself.'
        : 'Interview created successfully!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error finishing interview:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete interview');
    } finally {
      setSaving(false);
    }
  };

  const progressPercentage = (currentStep / steps.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {interview ? 'Edit Interview' : 'Create New Interview'}
          </DialogTitle>
          <DialogDescription>
            Step {currentStep} of {steps.length}: {steps[currentStep - 1].description}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progressPercentage} />
          <div className="flex justify-between text-xs text-muted-foreground">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex items-center gap-1 ${
                  step.id === currentStep
                    ? 'text-primary font-semibold'
                    : step.id < currentStep
                    ? 'text-green-600'
                    : ''
                }`}
              >
                {step.id < currentStep && <CheckCircle2 className="w-3 h-3" />}
                <span>{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Step Content */}
        <div className="py-4">
          {currentStep === 1 && (
            <ExpertSelector
              selectedExpertId={formData.expert_id}
              onSelect={(expert, demoMode) => {
                setIsDemoMode(demoMode || false);
                setFormData({
                  ...formData,
                  expert_id: expert.id,
                  demo_mode: demoMode || false
                });
              }}
              showDemoOption={true}
            />
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <LanguageSelector
                value={formData.language || 'en'}
                onChange={(language) => setFormData({ ...formData, language })}
              />

              {formData.journalist_id && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Journalist selected! Click "Next" to continue.
                  </AlertDescription>
                </Alert>
              )}
              <JournalistGallery
                selectable={true}
                showEditButton={false}
                showFilters={true}
                selectedJournalistId={formData.journalist_id}
                sectorIds={selectedSite?.sector_ids || (selectedSite?.sector_id ? [selectedSite.sector_id] : undefined)}
                onSelect={(journalist) => {
                  setFormData({ ...formData, journalist_id: journalist.id });
                  console.log('Journalist selected:', journalist.id);
                }}
              />
            </div>
          )}

          {currentStep === 3 && (
            <ApproachSelector
              approach={formData.approach || 'product_deep_dive'}
              approachData={formData.approach_data || {}}
              onChange={(approach, data) =>
                setFormData({ ...formData, approach, approach_data: data })
              }
              expertId={formData.expert_id}
            />
          )}

          {currentStep === 4 && (
            <QuestionCurator
              questions={formData.generated_questions || []}
              selectedQuestions={formData.selected_questions || []}
              onChange={(questions) =>
                setFormData({ ...formData, selected_questions: questions })
              }
            />
          )}

          {currentStep === 5 && (
            <BriefingViewer
              briefing={formData.briefing_content || ''}
              expertName={formData.expert_name || ''}
              journalistName={'Journalist'} // TODO: Get from journalist data
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between border-t pt-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || saving || generating}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || generating}>
              Cancel
            </Button>

            {currentStep < 5 ? (
              <Button
                onClick={handleNext}
                disabled={!canProgress() || saving || generating}
              >
                {generating ? (
                  currentStep === 3 ? 'Generating Questions...' : 'Generating Briefing...'
                ) : saving ? (
                  'Saving...'
                ) : currentStep === 3 ? (
                  <>
                    Generate Questions
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? 'Saving...' : 'Finish & Create Interview'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
